import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Paper,
  Tooltip
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Chatbot } from '../../types/chatbot';
import { API_URL } from '../../config';

interface InstallationDialogProps {
  open: boolean;
  onClose: () => void;
  selectedChatbot: Chatbot | null;
  copySuccess: boolean;
  handleCopyCode: () => Promise<void>;
}

const InstallationDialog: React.FC<InstallationDialogProps> = ({
  open,
  onClose,
  selectedChatbot,
  copySuccess,
  handleCopyCode
}) => {
  
  const getInstallationCode = (chatbotId: string) => {
    return `<script>
  (function() {
      const config = {
          chatbotId: '${chatbotId}',  // Your chatbot's unique ID
          serverUrl: '${API_URL}' 
      };
  
      const script = document.createElement('script');
      script.src = \`\${config.serverUrl}/widget.js\`;
      script.async = true;
      script.onload = function() {
          if (window.Glowspirit) {
              window.Glowspirit.init(config);
          } else {
              console.error('Glowspirit script failed to load.');
          }
      };
      
      document.body.appendChild(script);
  })();
</script>`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Installation Instructions</DialogTitle>
      <DialogContent>
        {selectedChatbot ? (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>
              {selectedChatbot.name} - Installation Code
            </Typography>
            <Typography paragraph>
              Copy the code below and paste it just before the closing <code>&lt;/body&gt;</code> tag on your website.
            </Typography>
            
            <Box sx={{ position: 'relative' }}>
              <Paper
                elevation={0}
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: '#f5f5f5',
                  overflow: 'auto',
                  maxHeight: 200,
                  fontFamily: 'monospace',
                  position: 'relative',
                  whiteSpace: 'pre',
                  mb: 3
                }}
              >
                {getInstallationCode(selectedChatbot._id)}
              </Paper>
              <Tooltip title={copySuccess ? "Copied!" : "Copy to clipboard"} placement="top">
                <IconButton
                  onClick={handleCopyCode}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'background.paper',
                    boxShadow: 1
                  }}
                >
                  {copySuccess ? <CheckCircleOutlineIcon color="success" /> : <ContentCopyIcon />}
                </IconButton>
              </Tooltip>
            </Box>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              Installation Steps
            </Typography>
            
            <Box component="ol" sx={{ pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>
                Open your website's HTML file or template.
              </Typography>
              <Typography component="li" sx={{ mb: 1 }}>
                Locate the closing <code>&lt;/body&gt;</code> tag.
              </Typography>
              <Typography component="li" sx={{ mb: 1 }}>
                Paste the installation code right before the <code>&lt;/body&gt;</code> tag.
              </Typography>
              <Typography component="li" sx={{ mb: 1 }}>
                Save the file and upload it to your server.
              </Typography>
              <Typography component="li" sx={{ mb: 1 }}>
                Load your website and test the chatbot.
              </Typography>
            </Box>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              For Content Management Systems (CMS)
            </Typography>
            
            <Typography paragraph>
              If you're using a CMS like WordPress, Wix, or Shopify, you'll need to add this code to your theme's footer.php file or through a custom HTML/JavaScript injection feature provided by your platform.
            </Typography>
            
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>
                <strong>WordPress</strong>: Use a header and footer plugin, or add to your theme's footer.php file.
              </Typography>
              <Typography component="li" sx={{ mb: 1 }}>
                <strong>Wix</strong>: Go to Settings &gt; Advanced &gt; Custom Code and add the script.
              </Typography>
              <Typography component="li" sx={{ mb: 1 }}>
                <strong>Shopify</strong>: Go to Online Store &gt; Themes &gt; Edit HTML/CSS &gt; theme.liquid and add before the closing body tag.
              </Typography>
            </Box>
          </Box>
        ) : (
          <Typography color="error">No chatbot selected</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {selectedChatbot && (
          <Button onClick={handleCopyCode} color="primary" variant="outlined" startIcon={<ContentCopyIcon />}>
            {copySuccess ? 'Copied!' : 'Copy Code'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InstallationDialog; 